// =====================================================
// HEADERS AND FOOTERS in the redline: each one the revised draft shows, paired with what the
// original showed on the same pages, and redlined in place.
// =====================================================

import { applyRevisionToAllRuns, flattenAcceptRevisions } from './docx-tracking';
import { addInsertMarkup } from './docx-markup';
import { NOTE_TEXT_NORM, fragmentText, withoutPageNumbers, noteParas, paraSpans } from './story-text';
import { hfLine, redlineParaSequence, redlineOnePara } from './story-redline';

/** Concatenated text of all header (or footer) parts of a package. */
export async function headerFooterText(zip: any, kind: 'header' | 'footer'): Promise<string> {
  if (!zip) return '';
  const names = Object.keys(zip.files).filter((n: string) => new RegExp(`^word/${kind}\\d*\\.xml$`).test(n)).sort();
  let out = '';
  for (const n of names) out += fragmentText(withoutPageNumbers(await zip.file(n).async('string'))) + '\n';
  return NOTE_TEXT_NORM(out);
}

/** A header or footer part with each of its paragraphs marked inserted where it stands, or
 *  null if one holds something other than text, tabs and fields (a drawing, a text box …). */
export function insertWholePart(revXml: string): string | null {
  let out = '', at = 0;
  for (const sp of paraSpans(revXml)) {
    const p = revXml.slice(sp.start, sp.end);
    let marked = p;
    if (NOTE_TEXT_NORM(fragmentText(p)) || /<w:(?:fldChar|fldSimple)\b/.test(p)) {
      // (the draft's own tracked changes are accepted first, as applyRevisionToAllRuns does)
      // (its content decides; a tracked change to its paragraph properties does not matter here)
      if (hfLine(flattenAcceptRevisions(p).replace(/<w:pPrChange\b[\s\S]*?<\/w:pPrChange>/g, '')) === null) return null;
      marked = applyRevisionToAllRuns(p, addInsertMarkup, 'insert');
    }
    out += revXml.slice(at, sp.start) + marked;
    at = sp.end;
  }
  return out + revXml.slice(at);
}

/**
 * A header or footer part with tables or content controls, its paragraphs redlined where they
 * stand (rebuilding the part from its paragraphs would drop the tables). Null unless both
 * drafts have the same paragraphs in the same tables, cells and controls.
 */
export function redlinePartInPlace(revXml: string, origXml: string): string | null {
  const rs = paraSpans(revXml), os = paraSpans(origXml);
  if (rs.length !== os.length) return null;
  const shape = (x: string, spans: { start: number; end: number }[]) => {
    let out = '', at = 0;
    for (const sp of spans) { out += x.slice(at, sp.start) + '¶'; at = sp.end; }
    out += x.slice(at);
    return (out.match(/<\/?w:(?:tbl|tr|tc|sdt|sdtContent)\b|¶/g) || []).join('');
  };
  if (shape(revXml, rs) !== shape(origXml, os)) return null;
  let out = '', at = 0;
  for (let i = 0; i < rs.length; i++) {
    const one = redlineOnePara(revXml.slice(rs[i].start, rs[i].end), origXml.slice(os[i].start, os[i].end), null);
    if (one === null) return null;
    out += revXml.slice(at, rs[i].start) + one;
    at = rs[i].end;
  }
  return out + revXml.slice(at);
}

/**
 * Header/footer references of a document, in document order, resolved to part
 * names via the document rels: [{ kind, type, part }].
 */
export function headerFooterRefs(docXml: string | null, relsXml: string | null): { kind: string; type: string; part: string }[] {
  const out: { kind: string; type: string; part: string }[] = [];
  if (!docXml || !relsXml) return out;
  const rels = new Map<string, string>();
  for (const m of relsXml.matchAll(/<Relationship\b[^>]*>/g)) {
    const id = (m[0].match(/ Id="([^"]*)"/) || [])[1];
    const target = (m[0].match(/ Target="([^"]*)"/) || [])[1];
    if (id && target) rels.set(id, 'word/' + target.replace(/^\//, '').replace(/^word\//, ''));
  }
  for (const m of docXml.matchAll(/<w:(header|footer)Reference\b[^>]*\bw:type="(\w+)"[^>]*\br:id="([^"]+)"/g)) {
    const part = rels.get(m[3]);
    if (part) out.push({ kind: m[1], type: m[2], part });
  }
  return out;
}

/** Which header and footer types a draft shows: a first-page one only with "Different first
 *  page" on (w:titlePg in a section), an even-page one only with "Different odd & even pages"
 *  on (w:evenAndOddHeaders in its settings). The others are in the file but never displayed. */
export type ShownTypes = { first: boolean; even: boolean };
export async function shownTypes(zip: any, docXml: string | null): Promise<ShownTypes> {
  const on = (x: string, tag: string) => new RegExp(`<w:${tag}\\b(?![^>]*\\bw:val="(?:0|false|off)")[^>]*>`).test(x);
  const settings: string = (await zip?.file('word/settings.xml')?.async('string')) || '';
  return { first: on(docXml || '', 'titlePg'), even: on(settings, 'evenAndOddHeaders') };
}
export const isShown = (type: string, shown: ShownTypes) => type === 'first' ? shown.first : type === 'even' ? shown.even : true;

/** The original's header or footer to compare a revised one with: what the original showed on
 *  those pages. The same kind and type at the same place when the original shows that type;
 *  its default one when it does not (its first or even pages showed the default). Undefined
 *  when the original showed none there. */
export function origCounterpart(oMap: Map<string, string[]>, kind: string, type: string, i: number, oShown: ShownTypes): string | undefined {
  const pick = (t: string) => { const l = oMap.get(kind + ':' + t) || []; return l.length ? l[Math.min(i, l.length - 1)] : undefined; };
  if (type === 'default' || isShown(type, oShown)) return (oMap.get(kind + ':' + type) || [])[i] ?? (type === 'default' ? undefined : pick(type));
  return pick('default');
}

/** A first-page or even-page header or footer only the original has, when the revised draft's
 *  default one (what those pages show now) reads the same: no change. */
export async function sameAsRevisedDefault(revZip: any, rMap: Map<string, string[]>, key: string, i: number, origXml: string): Promise<boolean> {
  const [kind, type] = key.split(':');
  if (type === 'default') return false;
  const def = rMap.get(kind + ':default') || [];
  if (!def.length) return false;
  const rx = await revZip.file(def[Math.min(i, def.length - 1)])?.async('string');
  return rx != null && NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(rx))) === NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(origXml)));
}

/**
 * Redline changed header/footer parts in place: pair the revised document's
 * header/footer references with the original's by kind+type occurrence, and
 * rewrite each pair whose text differs with a word-level redline. Returns the
 * number of parts redlined and the number that differ but could NOT be safely
 * redlined (structural content or no counterpart) — those get the summary warning.
 */
export async function redlineHeaderFooters(newZip: any, origZip: any, revZip: any,
    origDocXml: string | null, revDocXml: string | null): Promise<{ redlined: number; unredlined: number; paired: number }> {
  const res = { redlined: 0, unredlined: 0, paired: 0 };
  if (!origZip || !revZip) return res;
  const [oRels, rRels] = await Promise.all([
    origZip.file('word/_rels/document.xml.rels')?.async('string'),
    revZip.file('word/_rels/document.xml.rels')?.async('string'),
  ]);
  const oRefs = headerFooterRefs(origDocXml, oRels || null);
  const rRefs = headerFooterRefs(revDocXml, rRels || null);
  // pair by (kind,type) occurrence index
  const byKey = (refs: typeof oRefs) => {
    const map = new Map<string, string[]>();
    for (const r of refs) {
      const k = r.kind + ':' + r.type;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r.part);
    }
    return map;
  };
  const oMap = byKey(oRefs), rMap = byKey(rRefs);
  const oShown = await shownTypes(origZip, origDocXml), rShown = await shownTypes(revZip, revDocXml);
  res.paired = oRefs.length + rRefs.length;
  const donePart = new Set<string>();
  for (const [key, rParts] of rMap) {
    const [kind, type] = key.split(':');
    if (!isShown(type, rShown)) continue;   // in the file, never displayed
    for (let i = 0; i < rParts.length; i++) {
      const revPart = rParts[i];
      if (donePart.has(revPart)) continue;
      donePart.add(revPart);
      const origPart = origCounterpart(oMap, kind, type, i, oShown);
      const revXmlFile = revZip.file(revPart);
      if (!revXmlFile) continue;
      const revXml: string = await revXmlFile.async('string');
      const origXml: string | null = origPart ? await origZip.file(origPart)?.async('string') : null;
      // (a page number Word last showed differently is not a change: withoutPageNumbers)
      const rText = NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(revXml)));
      const oText = origXml != null ? NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(origXml))) : null;
      if (oText === null) {
        // a header or footer only the revised draft has (page numbers added, say): all of it
        // inserted, when it holds only text, tabs and fields
        if (!NOTE_TEXT_NORM(fragmentText(revXml)) && !/<w:(?:fldChar|fldSimple)\b/.test(revXml)) continue;
        const xml = insertWholePart(revXml);
        if (xml === null) { res.unredlined++; continue; }
        newZip.file(revPart, xml);
        res.redlined++;
        continue;
      }
      if (rText === oText) continue;
      // a part with tables or content controls: redline its paragraphs where they stand
      if (/<w:(?:tbl|sdt)\b/.test(revXml + origXml!)) {
        const xml = redlinePartInPlace(revXml, origXml!);
        if (xml === null) { res.unredlined++; continue; }
        newZip.file(revPart, xml);
        res.redlined++;
        continue;
      }
      // rewrite the part's paragraphs with a redline
      const rootOpen = (revXml.match(/<w:(?:hdr|ftr)\b[^>]*>/) || [])[0];
      const rootClose = revXml.includes('</w:hdr>') ? '</w:hdr>' : '</w:ftr>';
      if (!rootOpen) { res.unredlined++; continue; }
      const declOffset = revXml.indexOf(rootOpen);
      const decl = revXml.slice(0, declOffset);
      const inner = redlineParaSequence(noteParas(revXml), noteParas(origXml!), null);
      if (inner === null) { res.unredlined++; continue; }
      newZip.file(revPart, decl + rootOpen + inner + rootClose);
      res.redlined++;
    }
  }
  // orig-only header content that vanished (rev has no counterpart of that kind/type)
  for (const [key, oParts] of oMap) {
    if (!isShown(key.split(':')[1], oShown)) continue;
    const rCount = isShown(key.split(':')[1], rShown) ? (rMap.get(key) || []).length : 0;
    for (let i = rCount; i < oParts.length; i++) {
      const origXml = await origZip.file(oParts[i])?.async('string');
      if (origXml && NOTE_TEXT_NORM(fragmentText(origXml)) && !(await sameAsRevisedDefault(revZip, rMap, key, i, origXml))) res.unredlined++;
    }
  }
  return res;
}

/**
 * The header and footer references of a section break copied from the original (a deleted
 * paragraph's, tagged FRH- as it was written: tagCrossRelIds). They name the original's parts by
 * the original's relationship ids, which in the redline (the revised draft's package) name other
 * parts, or none. Each is pointed at the revised draft's header or footer of its kind that reads as
 * the original's did (page numbers aside), the one of the same part name first, or else left out,
 * and the section shows the headers of the section before it. Before, a reference kept its number
 * whenever the revised draft's relationship of that number was a header or footer, often another
 * one, and the section showed the wrong header.
 */
export async function resolveSectionRefs(newZip: any, origZip: any, revZip: any): Promise<void> {
  const doc: string | undefined = await newZip.file('word/document.xml')?.async('string');
  if (!doc || !/r:id="FRH-/.test(doc)) return;
  const relsOf = async (zip: any): Promise<Map<string, { kind: string; part: string }>> => {
    const map = new Map<string, { kind: string; part: string }>();
    const xml: string | undefined = zip ? await zip.file('word/_rels/document.xml.rels')?.async('string') : undefined;
    for (const m of (xml || '').matchAll(/<Relationship\b[^>]*>/g)) {
      const id = (m[0].match(/ Id="([^"]*)"/) || [])[1], target = (m[0].match(/ Target="([^"]*)"/) || [])[1];
      const kind = (m[0].match(/ Type="[^"]*\/([a-zA-Z]+)"/) || [])[1];
      if (id && target && kind) map.set(id, { kind, part: 'word/' + target.replace(/^\//, '').replace(/^word\//, '') });
    }
    return map;
  };
  const textOf = async (zip: any, part: string): Promise<string | null> => {
    const xml: string | undefined = zip ? await zip.file(part)?.async('string') : undefined;
    return xml == null ? null : NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(xml)));
  };
  const origRels = await relsOf(origZip), outRels = await relsOf(newZip);
  const resolved = new Map<string, string | null>();
  const resolve = async (kind: string, origId: string): Promise<string | null> => {
    const key = kind + ':' + origId;
    if (resolved.has(key)) return resolved.get(key)!;
    let found: string | null = null;
    const o = origRels.get(origId);
    const want = o && o.kind === kind ? await textOf(origZip, o.part) : null;
    if (want != null) {
      const candidates = [...outRels].filter(([, r]) => r.kind === kind)
        .sort(([, a], [, b]) => Number(b.part === o!.part) - Number(a.part === o!.part));
      for (const [id, r] of candidates) if ((await textOf(revZip, r.part)) === want) { found = id; break; }
    }
    resolved.set(key, found);
    return found;
  };
  let out = '';
  let at = 0;
  for (const m of doc.matchAll(/<w:(header|footer)Reference\b[^>]*?\br:id="FRH-([^"]+)"[^>]*\/>/g)) {
    const id = await resolve(m[1], m[2]);
    out += doc.slice(at, m.index!) + (id == null ? '' : m[0].replace(`"FRH-${m[2]}"`, `"${id}"`));
    at = m.index! + m[0].length;
  }
  newZip.file('word/document.xml', out + doc.slice(at));
}
